import React, { useState, useEffect } from 'react';
import { useAppDispatch } from 'redux/store';
import { useSelector } from 'react-redux';
import { createUser, updateUser, fetchUser } from '../../../../redux/slices/userSlice';
import { Form, Button, Row, Col, Alert, Tabs, Tab, Spinner } from 'react-bootstrap';
import PasswordChecklist from 'react-password-checklist';
import { RootState } from '../../../../redux/store';
import { useTranslation } from 'react-i18next';
import { getZones } from '../../../../redux/slices/lists/zonesSlice';
import PrivilegesCheckboxGroup from './usersPrivileges';
import AssignmentToggleList from '../AssignmentToggleList';
import { haveSameIds } from 'helpers/assignmentUtils';
import { getErrorMessage } from 'helpers/getErrorMessage';
import api from 'services/api';
import { toast } from 'react-toastify';

interface AvailableRole {
  role_id: number;
  name: string;
  ssoroleid?: string | null;
  assigned?: boolean;
  sso?: boolean;
}

interface EditUserProps {
  userId: number | null;
  closeModal: () => void;
}

const fetchAllRoles = async () => {
  const limit = 200;
  let page = 1;
  const roles: any[] = [];

  while (true) {
    const response = await api.get('/roles', {
      params: { limit, page },
    });
    const pageRoles = Array.isArray(response.data?.roles) ? response.data.roles : Array.isArray(response.data) ? response.data : [];

    roles.push(...pageRoles);

    const totalPages = Number(response.data?.pagination?.total_pages || 0);
    if (totalPages ? page >= totalPages : pageRoles.length < limit) {
      break;
    }

    page += 1;
  }

  return roles;
};

const EditUser = ({ userId, closeModal }: EditUserProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { users } = useSelector((state: RootState) => state.user);
  const currentUserId = useSelector((state: RootState) => state.auth?.userData?.user_id);
  const zones = useSelector((state: RootState) => state.listOfZones?.zones || []);

  // Fetch user if editing
  const existingUser = userId ? users.find((user) => user.user_id === userId) : null;

  const [username, setUsername] = useState(existingUser?.username || '');
  const [password, setPassword] = useState('');
  const [passwordAgain, setPasswordAgain] = useState('');
  const [mail, setMail] = useState(existingUser?.mail || '');
  const [public_key, setPublic_key] = useState(existingUser?.public_key || '');
  const [userConfig, setUserConfig] = useState({
    allow_connection_via: {
      web_ui: existingUser?.user_config?.allow_connection_via?.web_ui || false,
      webdav: existingUser?.user_config?.allow_connection_via?.webdav || false,
      rest: existingUser?.user_config?.allow_connection_via?.rest || false,
      as2: existingUser?.user_config?.allow_connection_via?.as2 || false,
      ftp: existingUser?.user_config?.allow_connection_via?.ftp || false,
      sftp: existingUser?.user_config?.allow_connection_via?.sftp || false,
    },
  });
  const existingUserSettings = existingUser?.user_settings || {};
  const [userSettings, setUserSettings] = useState(existingUserSettings);
  const [defaultZoneName, setDefaultZoneName] = useState<string>(
    typeof existingUserSettings?.default_zone_name === 'string' ? existingUserSettings.default_zone_name : ''
  );
  const [availableRoles, setAvailableRoles] = useState<AvailableRole[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [initialWritableRoleIds, setInitialWritableRoleIds] = useState<number[]>([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [rolesAccessDenied, setRolesAccessDenied] = useState(false);
  const [rolesLoadError, setRolesLoadError] = useState<string | null>(null);
  const [userPrivileges, setUserPrivileges] = useState<any[]>([]);
  const [privilegeFilter, setPrivilegeFilter] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const sortedZones = [...zones].sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    dispatch(getZones());
  }, [dispatch]);

  useEffect(() => {
    const run = async () => {
      setAvailableRoles([]);
      setSelectedRoleIds([]);
      setInitialWritableRoleIds([]);
      setRolesLoaded(false);
      setRolesAccessDenied(false);
      setRolesLoadError(null);

      try {
        const roles = userId ? (await api.get(`/users/${userId}/roles`)).data?.roles || [] : await fetchAllRoles();

        const normalizedRoles = roles.map((role: any) => ({
          role_id: role.role_id,
          name: role.name,
          ssoroleid: role.ssoroleid,
          assigned: Boolean(role.assigned),
          sso: Boolean(role.sso),
        }));
        const writableRoleIds = normalizedRoles.filter((role) => role.assigned).map((role) => role.role_id);

        setAvailableRoles(normalizedRoles);
        setSelectedRoleIds(writableRoleIds);
        setInitialWritableRoleIds(writableRoleIds);
        setRolesLoaded(true);
      } catch (error: any) {
        if (error?.response?.status === 403) {
          setRolesAccessDenied(true);
          return;
        }

        console.error('Failed to fetch roles:', error);
        setRolesLoadError(getErrorMessage(error?.response?.data, t('errors.failedToLoadRoles')));
      }
    };

    run();
  }, [t, userId]);

  useEffect(() => {
    const run = async () => {
      if (!userId) {
        return;
      }

      const result = await dispatch(fetchUser(userId) as any);
      if (fetchUser.fulfilled.match(result)) {
        const user = result.payload;
        const settings = user.user_settings || {};

        setUsername(user.username || '');
        setMail(user.mail || '');
        setPublic_key(user.public_key || '');
        setUserConfig({
          allow_connection_via: {
            web_ui: user.user_config?.allow_connection_via?.web_ui || false,
            webdav: user.user_config?.allow_connection_via?.webdav || false,
            rest: user.user_config?.allow_connection_via?.rest || false,
            as2: user.user_config?.allow_connection_via?.as2 || false,
            ftp: user.user_config?.allow_connection_via?.ftp || false,
            sftp: user.user_config?.allow_connection_via?.sftp || false,
          },
        });
        setUserSettings(settings);
        setDefaultZoneName(typeof settings?.default_zone_name === 'string' ? settings.default_zone_name : '');
        setUserPrivileges(user.privileges || []);
        setSaveError(null);
      }
      if (fetchUser.rejected.match(result)) {
        console.error('Fetch user failed:', result.payload);
      }
    };

    run();
  }, [dispatch, userId]);

  const handleSave = async () => {
    setSaveError(null);

    if (!userId && !password) {
      setSaveError(t('errors.passwordRequired'));
      return;
    }

    const nextUserSettings = {
      ...userSettings,
      default_zone_name: defaultZoneName || null,
    };

    const userPayload = {
      username,
      password: password ? password : undefined,
      mail,
      public_key,
      user_config: userConfig,
      user_settings: nextUserSettings,
      privileges: userPrivileges,
    };

    let result;
    if (userId) {
      result = await dispatch(updateUser({ ...userPayload, user_id: userId }) as any);
    } else {
      result = await dispatch(createUser(selectedRoleIds.length ? { ...userPayload, role_ids: selectedRoleIds } : userPayload) as any);
    }

    if (updateUser.fulfilled.match(result) || createUser.fulfilled.match(result)) {
      if (userId && rolesLoaded && !rolesAccessDenied && !haveSameIds(selectedRoleIds, initialWritableRoleIds)) {
        try {
          const rolesResponse = await api.put(`/users/${userId}/roles`, {
            role_ids: selectedRoleIds,
          });
          const roles = Array.isArray(rolesResponse.data?.roles) ? rolesResponse.data.roles : [];
          const normalizedRoles = roles.map((role: any) => ({
            role_id: role.role_id,
            name: role.name,
            ssoroleid: role.ssoroleid,
            assigned: Boolean(role.assigned),
            sso: Boolean(role.sso),
          }));
          const writableRoleIds = normalizedRoles.filter((role) => role.assigned).map((role) => role.role_id);

          setAvailableRoles(normalizedRoles);
          setSelectedRoleIds(writableRoleIds);
          setInitialWritableRoleIds(writableRoleIds);
        } catch (error: any) {
          setSaveError(getErrorMessage(error?.response?.data, t('errors.failedToSaveUserRoles')));
          return;
        }
      }

      if (updateUser.fulfilled.match(result)) {
        const toastId = 'data-update-user';
        if (!toast.isActive(toastId)) {
          toast.success(t('usersForm.updatedSuccessfully'), { toastId });
        }
      } else {
        const toastId = 'data-create-user';
        if (!toast.isActive(toastId)) {
          toast.success(t('usersForm.createdSuccessfully'), { toastId });
        }
      }
      closeModal();
      return;
    }

    setSaveError(getErrorMessage(result?.payload, t('errors.failedToSaveUser')));
  };

  const handlePrivilegeToggle = (privilege_id: number, zone_id: number) => {
    setUserPrivileges((prev) =>
      prev.map((priv) => (priv.privilege_id === privilege_id && priv.zone_id === zone_id ? { ...priv, assigned: !priv.assigned } : priv))
    );
  };

  const handleToggleAllZonePrivileges = (zoneId: number, assign: boolean) => {
    const visiblePrivilegeKeys = new Set(
      filteredUserPrivileges.filter((priv) => priv.zone_id === zoneId).map((priv) => `${priv.privilege_id}:${priv.zone_id}`)
    );

    setUserPrivileges((prev) => prev.map((priv) => (visiblePrivilegeKeys.has(`${priv.privilege_id}:${priv.zone_id}`) ? { ...priv, assigned: assign } : priv)));
  };

  // const updateZoneForPrivilege = (id: number, newZoneId: number) => {
  //   setUserPrivileges((prev) =>
  //     prev.map((priv) =>
  //       priv.privilege_id === id ? { ...priv, zone_id: newZoneId } : priv
  //     )
  //   );
  // };

  const handleConfigChange = (key: string) => {
    setUserConfig((prevConfig) => ({
      ...prevConfig,
      allow_connection_via: {
        ...prevConfig.allow_connection_via,
        [key]: !prevConfig.allow_connection_via[key],
      },
    }));
  };

  const handleRoleToggle = (roleId: number) => {
    if (availableRoles.some((role) => role.role_id === roleId && role.sso)) {
      return;
    }

    setSelectedRoleIds((prev) => (prev.includes(roleId) ? prev.filter((selectedRoleId) => selectedRoleId !== roleId) : [...prev, roleId]));
  };

  const roleAssignmentOptions = availableRoles.map((role) => ({
    id: role.role_id,
    label: role.name || role.ssoroleid || String(role.role_id),
    detail: role.ssoroleid && role.name ? role.ssoroleid : null,
    badge: role.ssoroleid ? 'SSO' : null,
    checked: Boolean(role.sso) || selectedRoleIds.includes(role.role_id),
    disabled: Boolean(role.sso),
  }));

  const assignedRoleCount = availableRoles.filter((role) => role.sso || selectedRoleIds.includes(role.role_id)).length;
  const filteredUserPrivileges = userPrivileges.filter((priv) => priv.name.toLowerCase().includes(privilegeFilter.toLowerCase()));

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        handleSave();
      }}
    >
      {saveError ? <Alert variant="danger">{saveError}</Alert> : null}

      <Tabs defaultActiveKey="general" id="edit-user-tabs" className="mb-3" variant="underline">
        <Tab eventKey="general" title={t('general')}>
          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm="2">
              {t('login.username')}
            </Form.Label>
            <Col sm="10">
              <Form.Control type="text" value={username} onChange={(e) => setUsername(e.target.value)} />
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm="2">
              {t('login.mail')}
            </Form.Label>
            <Col sm="10">
              <Form.Control type="email" value={mail} onChange={(e) => setMail(e.target.value)} />
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm="2">
              Password
            </Form.Label>
            <Col sm="10">
              <Form.Control type="password" onChange={(e) => setPassword(e.target.value)} />
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm="2">
              Password Again
            </Form.Label>
            <Col sm="10">
              <Form.Control type="password" onChange={(e) => setPasswordAgain(e.target.value)} />
            </Col>
          </Form.Group>

          <PasswordChecklist
            rules={['minLength', 'specialChar', 'number', 'capital', 'match']}
            minLength={5}
            value={password}
            valueAgain={passwordAgain}
            onChange={(isValid) => {}}
          />

          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm="2">
              Public Key
            </Form.Label>
            <Col sm="10">
              <Form.Control type="text" value={public_key} onChange={(e) => setPublic_key(e.target.value)} />
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm="2">
              Allow Connection via
            </Form.Label>
            <Col sm="10">
              {Object.keys(userConfig.allow_connection_via).map((key) => (
                <Form.Check
                  key={key}
                  type="checkbox"
                  label={key.toUpperCase()}
                  checked={userConfig.allow_connection_via[key]}
                  onChange={() => handleConfigChange(key)}
                />
              ))}
            </Col>
          </Form.Group>

          <h5 className="mt-4">DEFAULT SETTINGS</h5>
          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm="2">
              Default Zone
            </Form.Label>
            <Col sm="10">
              <Form.Select value={defaultZoneName} onChange={(e) => setDefaultZoneName(e.target.value)}>
                <option value="">None</option>
                {sortedZones.map((zone) => (
                  <option key={zone.zone_id} value={zone.name}>
                    {zone.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Form.Group>
        </Tab>

        <Tab eventKey="privileges" title={t('common.privileges')}>
          {!userId ? (
            <p className="text-muted m-3">{t('usersForm.privilegesAvailableAfterCreate')}</p>
          ) : userPrivileges.length > 0 ? (
            <>
              <Form.Group as={Row} className="mb-3" controlId="formUserPrivilegeFilter">
                <Form.Label column sm="2">
                  {t('roles.filterPrivileges')}
                </Form.Label>
                <Col sm="10">
                  <Form.Control type="text" placeholder={t('search') + '...'} value={privilegeFilter} onChange={(e) => setPrivilegeFilter(e.target.value)} />
                </Col>
              </Form.Group>

              <PrivilegesCheckboxGroup
                privileges={filteredUserPrivileges}
                zones={zones}
                onTogglePrivilege={handlePrivilegeToggle}
                onToggleAllZonePrivileges={handleToggleAllZonePrivileges}
              />
            </>
          ) : (
            <p className="text-muted m-3">{t('common.noPrivilegesAvailable')}</p>
          )}
        </Tab>

        {!rolesAccessDenied ? (
          <Tab eventKey="roles" title={t('usersForm.roles')}>
            {rolesLoadError ? (
              <Alert variant="danger">{rolesLoadError}</Alert>
            ) : !rolesLoaded ? (
              <div className="d-flex justify-content-center align-items-center m-4">
                <Spinner animation="border" role="status" size="sm" />
              </div>
            ) : (
              <AssignmentToggleList
                options={roleAssignmentOptions}
                selectedIds={selectedRoleIds}
                onToggle={handleRoleToggle}
                searchPlaceholder={t('usersForm.searchRoles')}
                assignedSummary={t('common.assignedSummary', {
                  assigned: assignedRoleCount,
                  total: availableRoles.length,
                })}
                emptyMessage={t('usersForm.noRolesFound')}
                checkboxPrefix="user-role"
              />
            )}
            {userId && currentUserId === userId ? <Form.Text muted>{t('common.selfAdminGuardNote')}</Form.Text> : null}
          </Tab>
        ) : null}
      </Tabs>

      <div className="d-flex justify-content-end gap-2">
        <Button variant="secondary" type="button" onClick={closeModal}>
          {t('button.close')}
        </Button>
        <Button variant="primary" type="submit">
          {userId ? t('button.save') : t('button.create')}
        </Button>
      </div>
    </Form>
  );
};

export default EditUser;
